import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "./cityPricing";

const menton: ChauffeurPage = {
  slug: "menton",
  title: "Chauffeur privé à Menton – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Menton : ville-frontière avec l’Italie, Fête du Citron, jardins et vieille ville, avec transfert aéroport de Nice en ~45 min.",
  keywords: [
    "chauffeur privé menton",
    "vtc menton",
    "chauffeur menton vintimille",
    "transfert menton aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Menton",
    subtitle:
      "La perle de la France à la porte de l’Italie : jardins, Fête du Citron et passages à Vintimille, avec un chauffeur dédié.",
    keywords: [
      "VTC Menton",
      "chauffeur Vintimille",
      "transfert aéroport Nice",
      "chauffeur Fête du Citron",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Menton",
    badges: ["Frontière italienne", "Fête du Citron", "~45 min de l’aéroport"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Menton",
    items: [
      {
        title: "À la frontière italienne",
        description:
          "Dernière ville avant la Ligurie : passage à Vintimille en quelques minutes, notamment pour son marché du vendredi, sans souci de stationnement de l’autre côté.",
      },
      {
        title: "Fête du Citron & jardins",
        description:
          "Jardin Biovès pendant la Fête du Citron, Serre de la Madone, Val Rahmeh : dépose au plus près des entrées, aux dates et horaires qui vous conviennent.",
      },
      {
        title: "Vieille ville & Cocteau",
        description:
          "Ruelles en pente vers la basilique Saint-Michel-Archange et le musée Jean Cocteau : le chauffeur vous laisse au pied du centre historique piéton.",
      },
      {
        title: "À ~45 minutes de l’aéroport",
        description:
          "La liaison la plus longue de nos villes vers l’aéroport de Nice : transfert direct avec suivi de vol et marge d’anticipation.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Menton",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Menton ⇄ aéroport de Nice en ~45 min, avec suivi de vol et horaire de départ anticipé pour cette liaison plus longue.",
      },
      {
        title: "Passage en Italie",
        text: "Excursions à Vintimille, Bordighera et San Remo, notamment pour le marché du vendredi, avec attente et retour organisés.",
      },
      {
        title: "Fête du Citron",
        text: "Navettes et dépose au plus près du Jardin Biovès pendant la Fête du Citron, malgré l’affluence et les rues fermées.",
      },
      {
        title: "Jardins de Menton",
        text: "Serre de la Madone, Val Rahmeh, Jardin Maria Serena et Fontana Rosa : circuit des jardins avec temps de visite inclus.",
      },
      {
        title: "Mise à disposition & alentours",
        text: "À l’heure ou à la journée pour relier la vieille ville, Roquebrune-Cap-Martin et Monaco, tout proche.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Vieille ville",
        description:
          "Ruelles et rampes vers la basilique Saint-Michel-Archange.",
      },
      {
        name: "Jardin Biovès",
        description: "Écrin de la Fête du Citron, entre mer et vieille ville.",
      },
      {
        name: "Serre de la Madone",
        description:
          "Jardin botanique en terrasses, dans les hauteurs de Menton.",
      },
      {
        name: "Val Rahmeh",
        description: "Jardin exotique et ses collections, près de Garavan.",
      },
      {
        name: "Musée Jean Cocteau",
        description: "Le Bastion et la collection, en bord de mer.",
      },
      {
        name: "Frontière de Vintimille",
        description:
          "Passage vers l’Italie par Menton-Garavan et Ponte San Luigi.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur pour la ville-frontière des jardins",
    subheading:
      "Menton mêle microclimat, jardins botaniques et proximité immédiate de l’Italie.",
    paragraphs: [
      "Blottie entre mer et montagne, Menton est la dernière ville française avant la Ligurie. Son microclimat nourrit des jardins d’exception — Serre de la Madone, Val Rahmeh — et sa Fête du Citron colore le Jardin Biovès en hiver ; un chauffeur vous conduit d’un site à l’autre en gérant l’affluence et le stationnement.",
      "KS Rent Services propose des forfaits fixes annoncés à l’avance — dont Menton ⇄ aéroport de Nice — et la mise à disposition à l’heure ou à la journée, avec devis immédiat. Nos chauffeurs bilingues français/anglais, basés sur la Côte d’Azur, sont notés 5,0/5 sur 38 avis Google.",
      "La frontière italienne est à portée : Vintimille et son marché, Bordighera, San Remo. Le chauffeur organise l’aller-retour, attend pendant vos achats et vous ramène à Menton ou à votre hôtel, sans que vous ayez à conduire de l’autre côté de la frontière.",
    ],
    bullets: [
      "Passage à Vintimille avec attente et retour organisés",
      "Forfaits fixes & mise à disposition, devis immédiat",
      "Chauffeurs bilingues FR/EN, 5,0/5 sur 38 avis Google",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Menton",
    steps: [
      "Indiquez votre programme : transfert aéroport, jardins, Fête du Citron ou passage en Italie.",
      "Nous confirmons le véhicule et le forfait ou la mise à disposition.",
      "Le chauffeur vous prend en charge à votre hôtel, villa ou dans la vieille ville.",
      "Ajustements en direct selon l’affluence, les horaires et le passage de frontière.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Menton"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis Menton ?",
      a: "Environ 45 minutes, la plus longue de nos liaisons, par l’A8. Le chauffeur suit votre vol et prévoit une marge d’anticipation adaptée à cette distance.",
    },
    {
      q: "Pouvez-vous nous emmener en Italie, à Vintimille ?",
      a: "Oui. Vintimille est à quelques minutes de Menton : nous organisons l’aller-retour, notamment pour le marché du vendredi, avec attente pendant vos achats et retour à Menton.",
    },
    {
      q: "Intervenez-vous pendant la Fête du Citron ?",
      a: "Oui. Pendant la Fête du Citron, nous assurons des navettes et une dépose au plus près du Jardin Biovès, malgré l’affluence et les rues fermées à la circulation.",
    },
    {
      q: "Proposez-vous un circuit des jardins de Menton ?",
      a: "Oui, en mise à disposition pour enchaîner Serre de la Madone, Val Rahmeh, Jardin Maria Serena et Fontana Rosa, avec temps de visite inclus.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez tôt pendant la Fête du Citron et en été ; le reste de l’année, les demandes de dernière minute sont traitées selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, à l’aise avec une clientèle internationale et les passages fréquents vers l’Italie.",
    },
    {
      q: "Quels véhicules proposez-vous pour Menton ?",
      a: "Berline Mercedes, van 7 places ou SUV selon le nombre de passagers, adaptés aux transferts longue liaison comme aux excursions transfrontalières.",
    },
  ],
  relatedLinks: [
    { label: "Navette aéroport de Nice", href: "/chauffeur/aeroport" },
    { label: "VTC à Monaco", href: "/chauffeur/monaco" },
    {
      label: "Réserver un chauffeur à Nice",
      href: "/chauffeur/nice",
    },
    {
      label: "VTC Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Menton",
    text: "Transfert aéroport, jardins, Fête du Citron ou passage en Italie : réservez votre chauffeur.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Menton 24h/24 — VTC & transferts",
    description:
      "Chauffeur privé et VTC à Menton : frontière italienne et Vintimille, Fête du Citron, jardins et transfert aéroport de Nice en ~45 min. 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/menton",
  },
};

export default menton;
